import { StaticImage } from "gatsby-plugin-image"
import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import getDummyText from "../dummyText"
import HeaderDiv from "./headerDiv"
import Text, { TextType } from "./text"
import { laptop } from "../util/mediaQueries"

const Row = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  @media ${laptop} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const SampleImage = () => (
  <StaticImage
    src="../images/client.png"
    alt={"Bla"}
    formats={["auto", "webp", "avif"]}
    imgStyle={{ objectFit: "fill" }}
    style={{ height: "100%", width: "100%", borderRadius: "10px 10px 0px 0px" }}
  />
)

// Position relative in order to make it work in safari
const BoxDiv = styled.div`
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  box-shadow: ${props => props.theme.shadow};
  &:hover {
    box-shadow: ${props => props.theme.shadowHovered};
  }
  transition: box-shadow 0.2s;
  background: ${props => props.theme.second};
  margin-left: 20px;
  margin-right: 20px;
  margin-bottom: 30px;
  margin-top: 30px;
  max-width: 550px;
  @media ${laptop} {
    width: 30%;
    margin: none;
    max-width: none;
  }
`

const ImgDiv = styled.div`
  border-radius: 10px 10px 0px 0px;
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 200px;
`

const TextDiv = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  text-align: center;
`

const BoxHeaderTextDiv = styled.p`
  margin-bottom: 10px;
  margin-top: 10px;
`

interface BoxProps {
  header: string
}

const Box = (props: BoxProps) => (
  <BoxDiv>
    <ImgDiv>
      <SampleImage />
    </ImgDiv>
    <TextDiv>
      <BoxHeaderTextDiv>
        <Text type={TextType.SubHeader}>{props.header}</Text>
      </BoxHeaderTextDiv>
      <Text type={TextType.Body}>{getDummyText(150)}</Text>
    </TextDiv>
  </BoxDiv>
)

// Server-side data fetching function
export async function getSteuernewsData() {
  try {
    // Fetch the actual HTML content directly from teaser.html
    // This bypasses the JavaScript loader and gets the content directly
    const response = await fetch('https://107802.mainfo.net/teaser.html');

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const htmlContent = await response.text();
    
    // Return the actual HTML content with styles
    return {
      success: true,
      content: htmlContent
    };
     } catch (error) {
     return {
       success: false,
       error: error instanceof Error ? error.message : 'Unknown error',
      content: `
        <div style="text-align: center; padding: 20px; color: #666;">
          <h3>Steuernews</h3>
          <p>Die aktuellen Steuernews konnten nicht geladen werden.</p>
          <p>Bitte versuchen Sie es später erneut.</p>
        </div>
      `
    };
  }
}

const SteuernewsIndex = () => {
  const [content, setContent] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const contentRef = useRef<HTMLDivElement>(null)

  // Helper function to parse JavaScript function calls from href
  const parseJavaScriptHref = (href: string | null): { functionName: string; params: string[] } | null => {
    if (!href || !href.startsWith('javascript:')) {
      return null;
    }
    
    // Remove 'javascript:' prefix and 'void(' wrapper
    const jsCode = href.replace(/^javascript:void\(/, '').replace(/\)$/, '');
    
    // Match function calls like: ervbeitragsaufruf('beitrag.html','DW20251201')
    const match = jsCode.match(/^(\w+)\((.*)\)$/);
    if (!match) {
      return null;
    }
    
    const functionName = match[1];
    const paramsString = match[2];
    
    // Parse parameters (simple string parsing for quoted strings)
    const params: string[] = [];
    let currentParam = '';
    let inQuotes = false;
    let quoteChar = '';
    
    for (let i = 0; i < paramsString.length; i++) {
      const char = paramsString[i];
      
      if ((char === '"' || char === "'") && (i === 0 || paramsString[i - 1] !== '\\')) {
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
          params.push(currentParam);
          currentParam = '';
          // Skip comma and whitespace
          while (i + 1 < paramsString.length && (paramsString[i + 1] === ',' || paramsString[i + 1] === ' ')) {
            i++;
          }
        }
      } else if (inQuotes) {
        currentParam += char;
      }
    }
    
    return { functionName, params };
  }

  useEffect(() => {
    const fetchContent = async () => {
      try {
        setIsLoading(true)
        
        // Use the server-side function directly
        const result = await getSteuernewsData()
        
        if (result.success) {
          setContent(result.content)
          setError('')
        } else {
          setError(result.error || 'Unknown error occurred')
        }
      } catch (err) {
        setError(`Failed to load content: ${err instanceof Error ? err.message : 'Unknown error'}`)
      } finally {
        setIsLoading(false)
      }
    }

    fetchContent()
  }, [])

  // Effect to load and execute scripts, and handle interactions after content is rendered
  useEffect(() => {
    if (!content || !contentRef.current) {
      return;
    }

    const container = contentRef.current;
    
    // Extract and execute script tags from the HTML
    const scripts = container.querySelectorAll('script');
    const loadedScripts: HTMLScriptElement[] = [];
    
    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script');
      
      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Copy content if inline script
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }
      
      // Handle src attribute - load external scripts
      if (oldScript.src) {
        const src = oldScript.src.startsWith('http') 
          ? oldScript.src 
          : `https://107802.mainfo.net/${oldScript.src}`;
        newScript.src = src;
      }
      
      // Replace old script with new one and execute
      oldScript.parentNode?.replaceChild(newScript, oldScript);
      loadedScripts.push(newScript);
    });
    
    // Load erv.js if it's referenced but not already loaded
    if (typeof window !== 'undefined' && !(window as any).erv) {
      const ervScript = document.createElement('script');
      ervScript.src = 'https://107802.mainfo.net/erv.js';
      ervScript.async = false; // Load synchronously to ensure it's available
      ervScript.onload = () => {
        // Script loaded - trigger any initialization if needed
        if (typeof (window as any).erv === 'function') {
          try {
            (window as any).erv();
          } catch (err) {
            // Ignore initialization errors
          }
        }
      };
      document.head.appendChild(ervScript);
    }
    
    // Handle links - but allow filter links to work natively
    const links = container.querySelectorAll('a');
    const clickHandlers: Array<{ element: Element; handler: (e: Event) => void }> = [];
    
    links.forEach((link) => {
      const href = link.getAttribute('href');
      const target = link.getAttribute('target');
      
      // Check if this is a filter link (inside responsive nav columns)
      const isFilterLink = link.closest('.erv-responsive-nav-column') !== null;
      
      // If it's a filter link, let it work natively - don't intercept
      if (isFilterLink) {
        return;
      }
      
      const clickHandler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Handle JavaScript links
        if (href && href.startsWith('javascript:')) {
          const parsed = parseJavaScriptHref(href);
          if (parsed) {
            // Handle different function types
            if (parsed.functionName === 'ervbeitragsaufruf' && parsed.params.length >= 2) {
              // Article link: ervbeitragsaufruf('beitrag.html','DW20251201')
              const articleUrl = `https://107802.mainfo.net/${parsed.params[0]}?id=${parsed.params[1]}`;
              window.open(articleUrl, '_blank');
            } else if (parsed.functionName === 'ervmonatsaufruf' && parsed.params.length >= 2) {
              // Month link: ervmonatsaufruf('aktuelles.html','0')
              const monthUrl = `https://107802.mainfo.net/${parsed.params[0]}?month=${parsed.params[1]}`;
              window.open(monthUrl, '_blank');
            } else if (parsed.functionName === 'ar' && parsed.params.length >= 1) {
              // Category link: ar('10alle.html','')
              const categoryUrl = `https://107802.mainfo.net/${parsed.params[0]}`;
              window.open(categoryUrl, '_blank');
            } else {
              // Unknown JavaScript function - try to execute it
              try {
                eval(parsed.functionName + '(' + parsed.params.map(p => `"${p}"`).join(',') + ')');
              } catch (err) {
                // If execution fails, allow default behavior
                return;
              }
            }
          } else {
            // Couldn't parse - try to execute the JavaScript directly
            try {
              const jsCode = href.replace(/^javascript:/, '');
              eval(jsCode);
            } catch (err) {
              // If execution fails, allow default behavior
              return;
            }
          }
        } else if (href && (href.startsWith('http://') || href.startsWith('https://'))) {
          // Regular HTTP link
          if (target === '_blank') {
            window.open(href, '_blank');
          } else {
            window.location.href = href;
          }
        } else if (href) {
          // Relative link
          const fullUrl = `https://107802.mainfo.net/${href}`;
          window.open(fullUrl, '_blank');
        }
      };
      
      link.addEventListener('click', clickHandler, true);
      clickHandlers.push({ element: link, handler: clickHandler });
    });
    
    // Handle search form submission
    const searchForm = container.querySelector('form#erv-archiv-searchbar') as HTMLFormElement;
    if (searchForm) {
      const formHandler = (e: Event) => {
        e.preventDefault();
        e.stopPropagation();
        
        const form = e.currentTarget as HTMLFormElement;
        const searchInput = form.querySelector('input[name="such"]') as HTMLInputElement;
        const searchTerm = searchInput?.value;
        
        if (searchTerm && searchTerm !== 'Suchbegriff eingeben...') {
          // Fetch filtered content
          const searchUrl = `https://107802.mainfo.net/aktuelles.html?such=${encodeURIComponent(searchTerm)}`;
          fetch(searchUrl)
            .then(response => response.text())
            .then(html => {
              // Update content state which will trigger re-initialization
              setContent(html);
            })
            .catch(() => {
              // Error handling - could show error message
            });
        }
      };
      
      searchForm.addEventListener('submit', formHandler);
    }
    
    // Handle form inputs and checkboxes for filter functionality
    const inputs = container.querySelectorAll('input, select, textarea');
    inputs.forEach((input) => {
      // Let native events work - don't interfere with filter functionality
      // Only handle the search input placeholder
      if (input.id === 'erv-search' || input.classList.contains('erv-archiv-suchfeld')) {
        input.addEventListener('focus', () => {
          if ((input as HTMLInputElement).value === 'Suchbegriff eingeben...') {
            (input as HTMLInputElement).value = '';
          }
        });
        input.addEventListener('blur', () => {
          if ((input as HTMLInputElement).value === '') {
            (input as HTMLInputElement).value = 'Suchbegriff eingeben...';
          }
        });
      }
    });
    
    // Handle label clicks (for responsive nav icon)
    const labels = container.querySelectorAll('label');
    labels.forEach((label) => {
      const htmlFor = label.getAttribute('for');
      if (htmlFor) {
        const targetElement = container.querySelector(`#${htmlFor}`) || document.getElementById(htmlFor);
        if (targetElement) {
          label.addEventListener('click', (e) => {
            // Trigger click on the associated input/button
            if (targetElement instanceof HTMLInputElement || targetElement instanceof HTMLButtonElement) {
              targetElement.click();
            }
          });
        }
      }
    });
    
    // Cleanup
    return () => {
      clickHandlers.forEach(({ element, handler }) => {
        element.removeEventListener('click', handler, true);
      });
    };
  }, [content])

  return (
    <>
      <HeaderDiv>
        <Text type={TextType.Header}>Steuernews</Text>
      </HeaderDiv>

      {/* Display content */}
      {error ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'red' }}>
          <p>Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              padding: '10px 20px', 
              marginTop: '10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      ) : content ? (
        <div 
          ref={contentRef}
          dangerouslySetInnerHTML={{ __html: content }} 
        />
      ) : (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{isLoading ? 'Loading news content...' : 'No content available'}</p>
          {isLoading && (
            <div style={{ marginTop: '20px' }}>
              <div style={{ 
                width: '40px', 
                height: '40px', 
                border: '4px solid #f3f3f3', 
                borderTop: '4px solid #007bff', 
                borderRadius: '50%', 
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }} />
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default SteuernewsIndex